/**
 * PAULOWNIA by Hayashi Wines - Global JavaScript
 * Dark Cinematic Theme - Background #0A0E14, Gold #C9A96E, Text #E8E4DF
 */

// ========================================
// Cart Drawer Functionality
// ========================================


// One cart operation owns the controls until completion, failure, or navigation.
window.StellaCartState = (() => {
  let owner = null;
  let generation = 0;
  const requests = new Set();
  const acquire = (extra = []) => {
    if (owner) return null;
    const token = {};
    const controls = [...new Set([...document.querySelectorAll('.cart-drawer [data-key], .cart-drawer__checkout, .cart-page [data-key], [data-stella-cart-remove], .cart-summary__checkout'), ...extra])];
    const previous = controls.map(control => ({ control, disabled: control.disabled, aria: control.getAttribute('aria-disabled') }));
    const release = () => {
      if (owner?.token !== token) return;
      previous.forEach(({ control, disabled, aria }) => {
        if ('disabled' in control) control.disabled = disabled;
        if (aria === null) control.removeAttribute('aria-disabled');
        else control.setAttribute('aria-disabled', aria);
      });
      owner = null;
    };
    owner = { token, release };
    controls.forEach(control => {
      if ('disabled' in control) control.disabled = true;
      control.setAttribute('aria-disabled', 'true');
    });
    return release;
  };
  const reset = () => {
    generation++;
    requests.forEach(controller => controller.abort());
    owner?.release();
  };
  const request = async (url, options = {}) => {
    const epoch = generation;
    const controller = new AbortController();
    requests.add(controller);
    let timer;
    const deadline = new Promise((_, reject) => {
      controller.signal.addEventListener('abort', () => reject(new Error('cart_request_cancelled')), { once: true });
      timer = setTimeout(() => {
        reject(new Error('cart_request_timeout'));
        controller.abort();
      }, 12000);
    });
    try {
      return await Promise.race([deadline, (async () => {
        const init = { ...options, credentials: 'same-origin', cache: 'no-store', signal: controller.signal };
        let response;
        let htmlFallback = false;
        try { response = await fetch(url, init); }
        catch (error) {
          // Read-only fallback. Never repeat an add/change whose outcome is uncertain.
          if (controller.signal.aborted || !url.endsWith('/cart.js') || (options.method && options.method !== 'GET')) throw error;
          response = await fetch(url.slice(0, -3), { ...init, headers: { Accept: 'text/html' } });
          htmlFallback = true;
        }
        let data;
        try {
          if (htmlFallback) {
            const page = new DOMParser().parseFromString(await response.text(), 'text/html');
            const state = page.querySelector('[data-stella-cart-state]');
            if (!state) throw new Error('cart_state_unavailable');
            data = JSON.parse(state.textContent);
            if (!Array.isArray(data.items) || !Number.isInteger(data.item_count)) throw new Error('invalid_cart');
          } else data = await response.json();
          if (Array.isArray(data?.items)) data.items = data.items.map(item => ({ ...item, properties: Array.isArray(item.properties) ? Object.fromEntries(item.properties) : (item.properties || {}) }));
        }
        catch (error) { if (response.ok) throw error; data = {}; }
        if (epoch !== generation) throw new Error('cart_request_cancelled');
        return { ok: response.ok, status: response.status, json: async () => data };
      })()]);
    } finally {
      clearTimeout(timer);
      requests.delete(controller);
    }
  };
  // The selected variant, selling plan, and properties identify a row.
const stellaCartPlanId = (item) => String(item.selling_plan_allocation?.selling_plan?.id || '');
const stellaCartProperties = (item) => {
  const properties = Array.isArray(item.properties) ? Object.fromEntries(item.properties) : (item.properties || {});
  const isDiners = properties._stella_campaign_code === 'DINERS_PREMIUM_2026' && (
    (String(item.variant_id) === '45564318580822' && stellaCartPlanId(item) === '2858975318') ||
    (String(item.variant_id) === '45568395870294' && stellaCartPlanId(item) === '2861498454')
  );
  return JSON.stringify(Object.keys(properties)
    .filter((key) => !(isDiners && (key === '_stella_consented_at' || key === '_stella_recurring_price_confirmed')))
    .sort().map((key) => [key, String(properties[key] ?? '')]));
};
const stellaCartSameItem = (item, target) =>
  String(item.variant_id) === String(target.variant_id) &&
  stellaCartPlanId(item) === stellaCartPlanId(target) &&
  stellaCartProperties(item) === stellaCartProperties(target);
const stellaCartResolveItem = (cart, target) => {
  if (!Array.isArray(cart?.items)) throw new Error('invalid_cart');
  const exact = cart.items.find((item) => item.key === target.key && stellaCartSameItem(item, target));
  if (exact) return exact;
  const matches = cart.items.filter((item) => stellaCartSameItem(item, target));
  return matches.length === 1 ? matches[0] : null;
};

  document.addEventListener('click', event => {
    if (owner && event.target.closest?.('.cart-summary__checkout, .cart-drawer__checkout')) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('submit', event => {
    if (owner && event.target.querySelector?.('[name="checkout"]')) event.preventDefault();
  }, true);
  window.addEventListener('pagehide', reset);
  return { acquire, request, reset, resolve: stellaCartResolveItem, get busy() { return Boolean(owner); }, get generation() { return generation; } };
})();

class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('.cart-drawer');
    if (!this.drawer) return;
    this.root = window.Shopify?.routes?.root || '/';
    this.busy = false;
    this.itemsByKey = new Map();
    this.messages = JSON.parse(this.drawer.querySelector('[data-cart-messages]')?.textContent || '{}');
    this.init();
  }

  init() {
    document.addEventListener('click', (event) => {
      const target = event.target.closest('button, a, .cart-drawer__overlay');
      if (!target) return;
      if (target.matches('[data-cart-toggle]')) {
        event.preventDefault();
        if (this.drawer.classList.contains('is-open')) this.close();
        else {
          this.open();
          if (!window.StellaCartState.busy) this.refreshCart();
        }
        return;
      }
      if (target.closest('.cart-drawer') && target.matches('.cart-drawer__close, .cart-drawer__continue, .cart-drawer__overlay')) {
        event.preventDefault();
        this.close();
        return;
      }
      const scope = target.closest('.cart-drawer, .cart-page');
      if (!scope) return;
      if (window.StellaCartState.busy && target.matches('.cart-drawer__checkout')) event.preventDefault();
      if (!target.dataset.key || !target.matches('[data-remove-item], [data-quantity-increase], [data-quantity-decrease]')) return;
      event.preventDefault();
      if (window.StellaCartState.busy) return;
      const key = target.dataset.key;
      if (target.matches('[data-remove-item]')) {
        this.updateQuantity(key, 0);
        return;
      }
      const input = Array.from(scope.querySelectorAll('[data-quantity-input]')).find(el => el.dataset.key === key);
      if (!input) return;
      const current = Number.parseInt(input.value, 10) || 1;
      const quantity = current + (target.matches('[data-quantity-increase]') ? 1 : -1);
      if (quantity > 0) this.updateQuantity(key, quantity);
    });
    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!input.matches('[data-quantity-input][data-key]') || !input.closest('.cart-drawer, .cart-page') || window.StellaCartState.busy) return;
      const quantity = Number(input.value);
      if (!Number.isInteger(quantity) || quantity < 1) {
        input.value = input.defaultValue || '1';
        this.showNotification(this.messages.invalid_quantity, 'error');
        return;
      }
      this.updateQuantity(input.dataset.key, quantity);
    });
    document.addEventListener('keydown', (event) => {
      if (!this.drawer.classList.contains('is-open')) return;
      if (event.key === 'Escape') this.close();
      if (event.key !== 'Tab') return;
      const controls = Array.from(this.drawer.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled)')).filter(el => el.getClientRects().length);
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });
    document.querySelectorAll('form[action$="/cart/add"]').forEach(form => {
      if (form.hasAttribute('data-stella-consent-form')) return;
      form.addEventListener('submit', event => {
        if (event.defaultPrevented) return;
        event.preventDefault();
        if (!window.StellaCartState.busy) this.addToCartAJAX(form);
      });
    });
    window.addEventListener('pageshow', event => {
      if (!event.persisted) return;
      this.setBusy(false);
      this.close();
      if (document.querySelector('.cart-page')) window.location.reload();
      else this.refreshCart();
    });
  }

  open() {
    if (this.drawer.classList.contains('is-open')) return;
    this.returnFocus = document.activeElement;
    this.previousOverflow = document.body.style.overflow;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.querySelectorAll('[data-cart-toggle]').forEach(el => el.setAttribute('aria-expanded', 'true'));
    document.body.style.overflow = 'hidden';
    this.drawer.querySelector('.cart-drawer__close')?.focus();
  }

  close() {
    if (!this.drawer.classList.contains('is-open')) return;
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-cart-toggle]').forEach(el => el.setAttribute('aria-expanded', 'false'));
    document.body.style.overflow = this.previousOverflow || '';
    if (this.returnFocus?.isConnected) this.returnFocus.focus();
  }

  setBusy(busy, form) {
    this.busy = busy;
    this.drawer.setAttribute('aria-busy', String(busy));
    if (busy) this.releaseControls = window.StellaCartState.acquire(form ? [...form.querySelectorAll('button[type="submit"], input[type="submit"]')] : []);
    else { this.releaseControls?.(); this.releaseControls = null; }
  }

  async request(endpoint, options = {}) {
    const response = await window.StellaCartState.request(this.root + endpoint, options);
    if (!response.ok) {
      const error = new Error('Cart request failed');
      error.status = response.status;
      const rejection = await response.json();
      error.userMessage = typeof rejection.description === 'string' ? rejection.description : typeof rejection.message === 'string' ? rejection.message : '';
      throw error;
    }
    return response.json();
  }

  async updateCart() {
    const cart = await this.request('cart.js');
    this.renderCart(cart);
    this.updateCartIcon(cart);
    return cart;
  }

  async refreshCart() {
    if (window.StellaCartState.busy) return;
    const generation = window.StellaCartState.generation;
    this.setBusy(true);
    try { await this.updateCart(); this.clearStatus(); }
    catch (error) { if (error.message !== 'cart_request_cancelled') this.showNotification(this.messages.refresh_error, 'error'); }
    finally { if (generation === window.StellaCartState.generation) this.setBusy(false); }
  }

  async addToCartAJAX(form) {
    if (window.StellaCartState.busy) return;
    const body = new FormData(form);
    const generation = window.StellaCartState.generation;
    this.setBusy(true, form);
    this.clearStatus();
    let added = false;
    try {
      if (form.dataset.singleMembership === 'true') {
        const cart = await this.request('cart.js');
        if (cart.items.some(item => item.selling_plan_allocation && (item.handle?.startsWith('the-stella-') || item.url?.includes('/products/the-stella-') || item.properties?._stella_membership_plan))) {
          this.renderCart(cart);
          this.updateCartIcon(cart);
          this.open();
          this.showNotification(this.messages.membership_exists, 'error');
          return;
        }
      }
      await this.request('cart/add.js', { method: 'POST', body });
      added = true;
      if (form.dataset.singleMembership === 'true') {
        await this.request('cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { _brand: 'THE-STELLA' } }) });
      }
      await this.updateCart();
      this.open();
      this.showNotification(this.messages.item_added);
    } catch (error) {
      if (error.message === 'cart_request_cancelled') return;
      if (added) window.location.assign(this.root + 'cart');
      else {
        this.showNotification(error.userMessage || this.messages.add_error, 'error');
      }
    } finally { if (generation === window.StellaCartState.generation) this.setBusy(false); }
  }

  renderCart(cart) {
    if (!Array.isArray(cart.items)) throw new Error('Invalid cart');
    const items = this.drawer.querySelector('.cart-drawer__items');
    const empty = this.drawer.querySelector('.cart-drawer__empty');
    const footer = this.drawer.querySelector('.cart-drawer__footer');
    if (!items || !empty || !footer) throw new Error('Cart markup unavailable');
    this.itemsByKey = new Map(cart.items.map(item => [item.key, item]));
    this.currency = cart.currency;
    items.innerHTML = cart.items.map(item => this.createCartItemHTML(item)).join('');
    items.hidden = cart.item_count === 0;
    empty.hidden = cart.item_count !== 0;
    footer.hidden = cart.item_count === 0;
    this.drawer.querySelector('.cart-drawer__subtotal-value').textContent = this.formatMoney(cart.total_price);
  }

  escape(value) {
    const element = document.createElement('div');
    element.textContent = String(value ?? '');
    return element.innerHTML.replaceAll('"', '&quot;').replaceAll("'", '&#39;');
  }

  createCartItemHTML(item) {
    const e = value => this.escape(value);
    const image = typeof item.image === 'string' ? item.image : item.image?.url || item.featured_image?.url;
    return `<li class="cart-item">
      ${image ? `<div class="cart-item__image"><img src="${e(image)}" alt="${e(item.title)}" width="80" height="100" loading="lazy"></div>` : ''}
      <div class="cart-item__details">
        <h3 class="cart-item__title"><a href="${e(item.url)}">${e(item.title)}</a></h3>
        ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__meta">${e(item.variant_title)}</div>` : ''}
        <div class="cart-item__price">${e(this.formatMoney(item.final_line_price))}</div>
        <div class="cart-item__actions">
          ${item.selling_plan_allocation ? `<span class="cart-item__fixed-quantity">${item.quantity === 1 ? e(this.messages.membership_quantity) : e(item.quantity + "契約（1契約にするには削除後に再追加してください）")}</span>` : `<div class="cart-item__quantity">
            <button type="button" data-quantity-decrease data-key="${e(item.key)}" aria-label="${e(this.messages.decrease)}">−</button>
            <input type="number" value="${item.quantity}" min="1" step="1" data-quantity-input data-key="${e(item.key)}" aria-label="${e(this.messages.quantity)}">
            <button type="button" data-quantity-increase data-key="${e(item.key)}" aria-label="${e(this.messages.increase)}">+</button>
          </div>`}
          <button type="button" class="cart-item__remove" data-remove-item data-key="${e(item.key)}" aria-label="${e(this.messages.remove)}: ${e(item.title)}">${e(this.messages.remove)}</button>
        </div>
      </div>
    </li>`;
  }

  async updateQuantity(key, quantity) {
    if (window.StellaCartState.busy || !Number.isInteger(quantity) || quantity < 0) return;
    const generation = window.StellaCartState.generation;
    this.setBusy(true);
    this.clearStatus();
    try {
      const row = Array.from(document.querySelectorAll('.cart-page [data-stella-cart-remove]')).find(el => el.dataset.cartKey === key);
      let target = this.itemsByKey.get(key);
      if (!target && row) target = { key, variant_id: row.dataset.cartVariant, selling_plan_allocation: { selling_plan: { id: row.dataset.cartPlan } }, properties: JSON.parse(row.dataset.cartProperties) };
      let cart;
      for (let attempt = 0; attempt < 2; attempt++) {
        cart = await this.request('cart.js');
        target ||= cart.items.find(item => item.key === key);
        const current = target && window.StellaCartState.resolve(cart, target);
        if (!current) break; // A removed/replaced/ambiguous item is never changed.
        try {
          cart = await this.request('cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: current.key, quantity }) });
          break;
        } catch (error) {
          if (attempt === 0 && (error.status === 400 || error.status === 422)) continue;
          throw error; // An uncertain network result must never repeat a mutation.
        }
      }
      if (document.querySelector('.cart-page')) { window.location.reload(); return; }
      this.renderCart(cart);
      this.updateCartIcon(cart);
      this.showNotification(quantity === 0 ? this.messages.item_removed : this.messages.updated);
      if (this.drawer.classList.contains('is-open')) {
        const next = Array.from(this.drawer.querySelectorAll('[data-quantity-input]')).find(el => el.dataset.key === key);
        (next || this.drawer.querySelector('.cart-drawer__close'))?.focus();
      }
    } catch (error) {
      if (error.message === 'cart_request_cancelled') return;
      document.querySelectorAll('.cart-page [data-quantity-input]').forEach(el => { el.value = el.defaultValue; });
      this.showNotification(error.userMessage || this.messages.update_error, 'error');
    } finally { if (generation === window.StellaCartState.generation) this.setBusy(false); }
  }

  updateCartIcon(cart) {
    document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; el.hidden = cart.item_count === 0; });
  }

  formatMoney(cents) {
    const currency = this.currency || window.Shopify?.currency?.active || 'JPY';
    return new Intl.NumberFormat(document.documentElement.lang || 'ja-JP', { style: 'currency', currency, minimumFractionDigits: currency === 'JPY' ? 0 : 2, maximumFractionDigits: currency === 'JPY' ? 0 : 2 }).format(cents / 100);
  }

  clearStatus() {
    const status = this.drawer.querySelector('[data-cart-status]');
    if (status) { status.hidden = true; status.textContent = ''; }
    document.querySelectorAll('[data-cart-notification]').forEach(el => el.remove());
  }

  showNotification(message, type = 'success') {
    if (!message) return;
    const status = this.drawer.querySelector('[data-cart-status]');
    if (status) { status.textContent = message; status.hidden = false; }
    if (this.drawer.classList.contains('is-open')) return;
    document.querySelectorAll('[data-cart-notification]').forEach(el => el.remove());
    const notification = document.createElement('div');
    notification.className = `notification notification--${type} is-visible`;
    notification.dataset.cartNotification = '';
    notification.textContent = message;
    notification.setAttribute('role', type === 'error' ? 'alert' : 'status');
    document.body.appendChild(notification);
    if (type === 'error') {
      const recovery = document.createElement('a');
      recovery.href = this.root + 'cart';
      recovery.textContent = ' 最新のカートを確認';
      recovery.style.textDecoration = 'underline';
      notification.appendChild(recovery);
    } else setTimeout(() => notification.remove(), 8000);
  }
}

// ========================================
// Mobile Menu Toggle
// ========================================

class MobileMenu {
  constructor() {
    this.menuToggle = document.querySelector('[data-menu-toggle]');
    this.menu = document.querySelector('[data-mobile-menu]');

    if (this.menuToggle && this.menu) {
      this.init();
    }
  }

  init() {
    this.menuToggle.addEventListener('click', () => this.toggle());

    // Close menu when clicking on a link
    this.menu.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', () => this.close());
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.menu.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  toggle() {
    if (this.menu.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.menu.classList.add('is-open');
    this.menuToggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.menu.classList.remove('is-open');
    this.menuToggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }
}

// ========================================
// Sticky Header
// ========================================

class StickyHeader {
  constructor() {
    this.header = document.querySelector('[data-sticky-header]');
    this.lastScrollY = 0;
    this.isScrolling = false;

    if (this.header) {
      this.init();
    }
  }

  init() {
    window.addEventListener('scroll', () => {
      if (!this.isScrolling) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          this.isScrolling = false;
        });
        this.isScrolling = true;
      }
    });
  }

  handleScroll() {
    const scrollY = window.scrollY;

    if (scrollY > 50) {
      this.header.classList.add('is-scrolled');
    } else {
      this.header.classList.remove('is-scrolled');
    }

    if (scrollY > this.lastScrollY) {
      // Scrolling down
      this.header.classList.add('is-hidden');
    } else {
      // Scrolling up
      this.header.classList.remove('is-hidden');
    }

    this.lastScrollY = scrollY;
  }
}

// ========================================
// Smooth Scroll for Anchor Links
// ========================================

class SmoothScroll {
  constructor() {
    this.init();
  }

  init() {
    document.querySelectorAll('a[href^="#"]').forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        if (href === '#') return;

        const target = document.querySelector(href);
        if (!target) return;

        e.preventDefault();

        const offset = 80; // Account for sticky header
        const top = target.getBoundingClientRect().top + window.scrollY - offset;

        window.scrollTo({
          top: top,
          behavior: 'smooth'
        });

        // Update URL without jumping
        window.history.pushState(null, null, href);
      });
    });
  }
}

// ========================================
// Lazy Loading Images
// ========================================

class LazyLoad {
  constructor() {
    this.init();
  }

  init() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;

            if (img.dataset.src) {
              img.src = img.dataset.src;
            }

            if (img.dataset.srcset) {
              img.srcset = img.dataset.srcset;
            }

            img.classList.add('is-loaded');
            observer.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px'
      });

      document.querySelectorAll('[data-src]').forEach(img => {
        imageObserver.observe(img);
      });
    } else {
      // Fallback for older browsers
      document.querySelectorAll('[data-src]').forEach(img => {
        if (img.dataset.src) img.src = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      });
    }
  }
}

// ========================================
// Product Quantity Selector
// ========================================

class QuantitySelector {
  constructor() {
    this.init();
  }

  init() {
    document.querySelectorAll('[data-quantity-selector]').forEach(selector => {
      const input = selector.querySelector('input');
      const decreaseBtn = selector.querySelector('[data-quantity-decrease]');
      const increaseBtn = selector.querySelector('[data-quantity-increase]');

      if (input) {
        input.addEventListener('change', () => {
          input.value = Math.max(1, parseInt(input.value) || 1);
        });
      }

      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', () => {
          const current = parseInt(input.value) || 1;
          if (current > 1) {
            input.value = current - 1;
            input.dispatchEvent(new Event('change'));
          }
        });
      }

      if (increaseBtn) {
        increaseBtn.addEventListener('click', () => {
          const current = parseInt(input.value) || 1;
          input.value = current + 1;
          input.dispatchEvent(new Event('change'));
        });
      }
    });
  }
}

// ========================================
// Predictive Search
// ========================================

class PredictiveSearch {
  constructor() {
    this.searchInput = document.querySelector('[data-predictive-search-input]');
    this.resultsContainer = document.querySelector('[data-predictive-search-results]');
    this.debounceTimer = null;

    if (this.searchInput) {
      this.init();
    }
  }

  init() {
    this.searchInput.addEventListener('input', (e) => {
      clearTimeout(this.debounceTimer);

      const query = e.target.value.trim();

      if (query.length < 2) {
        this.clearResults();
        return;
      }

      this.debounceTimer = setTimeout(() => {
        this.search(query);
      }, 300);
    });

    // Close results when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-predictive-search]')) {
        this.clearResults();
      }
    });
  }

  search(query) {
    const encodedQuery = encodeURIComponent(query);

    fetch(`/search/suggest.json?q=${encodedQuery}&resources[type]=product&resources[limit]=8`)
      .then(response => response.json())
      .then(data => this.renderResults(data))
      .catch(error => console.error('Search error:', error));
  }

  renderResults(data) {
    if (!data.resources || !data.resources.results) return;

    const products = data.resources.results.products || [];

    if (products.length === 0) {
      this.resultsContainer.innerHTML = '<p class="search-no-results">No results found</p>';
      return;
    }

    const html = products.map(product => `
      <a href="${product.url}" class="search-result-item">
        <span class="search-result-title">${product.title}</span>
      </a>
    `).join('');

    this.resultsContainer.innerHTML = html;
  }

  clearResults() {
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = '';
    }
  }
}

// ========================================
// Initialize on DOM Ready
// ========================================

document.addEventListener('DOMContentLoaded', function() {
  new CartDrawer();
  new MobileMenu();
  new StickyHeader();
  new SmoothScroll();
  new LazyLoad();
  new QuantitySelector();
  new PredictiveSearch();

  console.log('PAULOWNIA Theme: All components initialized');
});

// ========================================
// Utility Functions
// ========================================

/**
 * Debounce function
 */
function debounce(func, delay) {
  let timeoutId;
  return function(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
}

/**
 * Throttle function
 */
function throttle(func, limit) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Format currency
 */
function formatCurrency(cents) {
  if (typeof cents === 'string') {
    cents = parseInt(cents);
  }
  const dollars = (cents / 100).toFixed(2);
  return `$${dollars}`;
}

/**
 * Parse query parameters
 */
function getQueryParam(param) {
  const params = new URLSearchParams(window.location.search);
  return params.get(param);
}
